// ---------------------------------------------------------------------------
// UpdateOrAddPaymentMethod
// ---------------------------------------------------------------------------
// • Renders a button (“Add payment method” or “Update card”).
// • On click → opens an in-place modal with <CreditCardForm/>.
// • If the user already has a card, the modal first displays the masked
//   details (•••• 1234  MM/YY) fetched from the new GET endpoint.
// • When the form is submitted it calls /payment-method or /switch-to-business
//   (via PaymentService) and, on success, invokes `onSaved()`.
//
//   Usage
//   -----
//   <UpdateOrAddPaymentMethod
//       userId={caseId}
//       userType={1}                       // 1 = client, 2 = accountant
//       hasPaymentMethod={status.hasArrangementPaymentMethod}
//       onSaved={refreshStatus}            // callback when card saved OK
//   />
// ---------------------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Modal,
    Box,
    Typography,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';

import CreditCardForm from './parts/CreditCardForm';
import PaymentService from '../../Services/payment.service';

/* ---------- modal style ---------- */
const MODAL = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    px: 4,
    py: 3
};

/* ---------- form validation ---------- */
const validate = (v) => {
    const errs = {};
    const num = v.cardNumber.replace(/\s+/g, '');

    if (!/^\d{13,19}$/.test(num)) errs.cardNumber = 'Invalid card number';
    const mm = +v.month,
        yy = +v.year;
    if (!mm || mm < 1 || mm > 12) errs.month = 'Invalid month';
    if (!yy || yy < 23 || yy > 99) errs.year = 'Invalid year';
    if (!/^\d{3}$/.test(v.cvv)) errs.cvv = 'CVV must be 3 digits';

    return errs;
};

/* ------------------------------------------------------------------------- */
const UpdateOrAddPaymentMethod = ({
    userId,
    userType, // 1 = client, 2 = accountant
    hasPaymentMethod,
    buttonVariant = 'outlined',
    onSaved = () => {},
    labelAdd,
    labelUpdate
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();

    /* -------- component state -------- */
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [ok, setOk] = useState(false);
    const [error, setError] = useState('');
    const [values, setValues] = useState({
        cardNumber: '',
        month: '',
        year: '',
        cvv: '',
        holder: ''
    });
    const [errs, setErrs] = useState({});
    const [cardInfo, setCardInfo] = useState(null); // { last4, expiryMonth, expiryYear }

    /* decide which part of the API we are calling */
    const service =
        +userType === 2 ? PaymentService.accountant : PaymentService.client;

    /* -------- input handlers -------- */
    const handleChange = (e) => {
        const { name, value } = e.target;
        const fmt = (s) =>
            s.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim();

        setValues((v) => ({
            ...v,
            [name]: name === 'cardNumber' ? fmt(value) : value
        }));
        setErrs((e) => ({ ...e, [name]: '' }));
    };

    /* -------- fetch current masked card when the modal opens -------- */
    useEffect(() => {
        if (!open || !hasPaymentMethod) {
            setCardInfo(null);
            return;
        }

        (async () => {
            try {
                const info = await service.getPaymentMethod(userId);
                setCardInfo(info); // { last4, expiryMonth, expiryYear }
            } catch {
                /* silent fail – leave cardInfo null */
            }
        })();
    }, [open, hasPaymentMethod, service, userId]);

    /* -------- submit handler -------- */
    const handleSubmit = async () => {
        const v = validate(values);
        if (Object.keys(v).length) {
            setErrs(v);
            return;
        }

        setBusy(true);
        setError('');
        const num = values.cardNumber.replace(/\s+/g, '');

        const dto = {
            UserId: userId,
            CardNumber: num,
            ExpiryMonth: +values.month,
            ExpiryYear: +values.year,
            CVV: values.cvv,
            CardHolderId: values.holder
        };

        try {
            await service.upsertPaymentMethod(dto);
            setOk(true);
            /* small delay so the user sees the success message */
            setTimeout(() => {
                setOpen(false);
                onSaved(); // let parent refresh status
                reset();
            }, 1200);
        } catch (ex) {
            setError(ex.message || t('PaymentFailed'));
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setValues({ cardNumber: '', month: '', year: '', cvv: '', holder: '' });
        setErrs({});
        setOk(false);
        setError('');
        setCardInfo(null);
    };

    /* -------- render -------- */
    return (
        <>
            <Button
                variant={buttonVariant}
                startIcon={<CreditCardIcon />}
                onClick={() => {
                    reset();
                    setOpen(true);
                }}
            >
                {hasPaymentMethod
                    ? labelUpdate ?? t('UpdateCard')
                    : labelAdd ?? t('AddPaymentMethod')}
            </Button>

            <Modal open={open} onClose={() => setOpen(false)} disableEscapeKeyDown>
                <Box sx={MODAL} dir={dir}>
                    <Typography variant="h6" gutterBottom>
                        {ok
                            ? t('PaymentMethodSaved')
                            : t('AddOrUpdateCard')}
                    </Typography>

                    <DialogContent dividers>
                        {/* Existing card masked info */}
                        {cardInfo && !ok && (
                            <Alert
                                severity="info"
                                icon={<CreditCardIcon fontSize="inherit" />}
                                sx={{ mb: 2 }}
                                
                            >
                                {t('CurrentCard')}  {cardInfo.last4}{' '} ••••
                                ({String(cardInfo.expiryMonth).padStart(2, '0')}/
                                {cardInfo.expiryYear})
                            </Alert>
                        )}

                        {/* Success message */}
                        {ok ? (
                            <Alert
                                severity="success"
                                icon={<CheckIcon fontSize="inherit" />}
                            >
                                {t('CardSavedSuccess')}
                            </Alert>
                        ) : (
                            <>
                                <CreditCardForm
                                    values={values}
                                    onChange={handleChange}
                                    disabled={busy}
                                    errors={errs}
                                />

                                {error && (
                                    <Alert
                                        severity="error"
                                        sx={{ mt: 2 }}
                                        icon={
                                            <ErrorIcon fontSize="inherit" />
                                        }
                                    >
                                        {error}
                                    </Alert>
                                )}
                            </>
                        )}
                    </DialogContent>

                    {!ok && (
                        <DialogActions>
                            <Button onClick={() => setOpen(false)}>
                                {t('Close')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={busy}
                                startIcon={
                                    busy && <CircularProgress size={20} />
                                }
                            >
                                {t('SaveCard')}
                            </Button>
                        </DialogActions>
                    )}
                </Box>
            </Modal>
        </>
    );
};

UpdateOrAddPaymentMethod.propTypes = {
    userId: PropTypes.string.isRequired,
    userType: PropTypes.number.isRequired, // 1 = client, 2 = accountant
    hasPaymentMethod: PropTypes.bool,
    buttonVariant: PropTypes.string,
    onSaved: PropTypes.func,
    labelAdd: PropTypes.string,
    labelUpdate: PropTypes.string
};

export default UpdateOrAddPaymentMethod;
